// frontend/src/pages/admin/AdminNotificationSettings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { settingsApi } from '../../api/settings';
import { 
    FaCommentDots, FaSave, FaSpinner, FaShieldAlt, FaEnvelope, FaPhone, FaCheckCircle, FaInfoCircle, FaUniversity 
} from 'react-icons/fa';

const MASTER_ADMIN_EMAILS = ["esekiegabriel@gmail.com", "thomasesekie@gmail.com"];

function AdminNotificationSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
      email_provider: 'google',
      sms_provider: 'kudi',
      admin_emails: Array(5).fill({ email: '', active: false }),
      admin_phones: Array(5).fill({ phone: '', active: false }),
      bank_accounts: Array(5).fill({ bank_name: '', account_number: '', account_name: '', active: false })
  });

  // --- SECURITY CHECK ---
  useEffect(() => {
      if (user && !MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
          alert("Access Denied: Master Admin Only.");
          navigate('/admin');
      } else {
          fetchSettings();
      }
  }, [user, navigate]);

  const fetchSettings = async () => {
      try {
          const data = await settingsApi.getAllSettings();
          const notifConfig = data.notification_config || {};
          
          // Ensure arrays have 5 slots
          const emails = notifConfig.admin_emails || [];
          while (emails.length < 5) emails.push({ email: '', active: false });
          
          const phones = notifConfig.admin_phones || [];
          while (phones.length < 5) phones.push({ phone: '', active: false });

          const banks = notifConfig.bank_accounts || [];
          while (banks.length < 5) banks.push({ bank_name: '', account_number: '', account_name: '', active: false });

          setConfig({
              email_provider: notifConfig.email_provider || 'google',
              sms_provider: notifConfig.sms_provider || 'kudi',
              admin_emails: emails.slice(0, 5),
              admin_phones: phones.slice(0, 5),
              bank_accounts: banks.slice(0, 5)
          });
      } catch (err) {
          console.error("Failed to load settings", err);
      } finally {
          setLoading(false);
      }
  };

  const handleEmailChange = (index, field, value) => {
      const newEmails = [...config.admin_emails];
      newEmails[index] = { ...newEmails[index], [field]: value };
      setConfig(prev => ({ ...prev, admin_emails: newEmails }));
  };

  const handlePhoneChange = (index, field, value) => {
      const newPhones = [...config.admin_phones];
      newPhones[index] = { ...newPhones[index], [field]: value };
      setConfig(prev => ({ ...prev, admin_phones: newPhones }));
  };

  const handleBankChange = (index, field, value) => {
      const newBanks = [...config.bank_accounts];
      newBanks[index] = { ...newBanks[index], [field]: value };
      setConfig(prev => ({ ...prev, bank_accounts: newBanks }));
  };

  const handleProviderChange = (e) => {
      setConfig(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
      setSaving(true);
      try {
          await settingsApi.updateSettings('notification_config', config);
          // --- UPDATED CLEARER MESSAGE ---
          alert("✅ Success! System Updated:\n\n• Users with CHECKED boxes are now Admins.\n• Users with UNCHECKED boxes have been removed from Admin.");
      } catch (err) {
          alert("Failed to save settings.");
      } finally {
          setSaving(false);
      }
  };

  if (loading) return <div className="p-10 text-center"><FaSpinner className="animate-spin text-3xl text-primary" /> Loading Settings...</div>;

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <div className="flex items-center gap-3 mb-8 border-b border-gray-200 pb-4">
          <div className="bg-purple-600 text-white p-3 rounded-lg shadow-lg">
              <FaCommentDots size={24} />
          </div>
          <div>
              <h1 className="text-2xl font-bold text-gray-800">Notification Settings</h1>
              <p className="text-sm text-purple-600 font-bold flex items-center gap-1">
                  <FaShieldAlt /> RESTRICTED AREA: Master Admin Only
              </p>
          </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          
          {/* --- PROVIDER SELECTION --- */}
          <div className="p-6 border-b border-gray-100 bg-gray-50">
              <h3 className="font-bold text-gray-800 mb-4">Provider Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* EMAIL PROVIDER SELECTION */}
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Email Provider</label>
                      <div className="flex flex-col gap-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="email_provider" value="google" checked={config.email_provider === 'google'} onChange={handleProviderChange} className="text-purple-600" />
                              <span className="text-sm font-medium">Google SMTP</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="email_provider" value="kudisms" checked={config.email_provider === 'kudisms'} onChange={handleProviderChange} className="text-purple-600" />
                              <span className="text-sm font-medium">KudiSMS Email</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="email_provider" value="termii" checked={config.email_provider === 'termii'} onChange={handleProviderChange} className="text-purple-600" />
                              <span className="text-sm font-medium">Termii Email</span>
                          </label>
                      </div>
                  </div>

                  {/* SMS PROVIDER SELECTION */}
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2">SMS Provider</label>
                      <div className="flex flex-col gap-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="sms_provider" value="kudisms" checked={config.sms_provider === 'kudisms'} onChange={handleProviderChange} className="text-purple-600" />
                              <span className="text-sm font-medium">KudiSMS (Recommended)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="sms_provider" value="termii" checked={config.sms_provider === 'termii'} onChange={handleProviderChange} className="text-purple-600" />
                              <span className="text-sm font-medium">Termii</span>
                          </label>
                      </div>
                  </div>
              </div>
          </div>

          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* --- ADMIN EMAILS --- */}
              <div>
                  <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                      <FaEnvelope className="text-blue-500" /> Admin Emails
                  </h3>
                  <div className="bg-blue-50 p-3 rounded text-xs text-blue-800 mb-4 flex items-start gap-2">
                      <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                      <p>
                          Enter emails to receive system alerts. 
                          <strong> Checking 'Active' grants Admin role. Unchecking it REVOKES Admin role immediately.</strong>
                      </p>
                  </div>

                  <div className="space-y-3">
                      {config.admin_emails.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                              <span className="text-xs font-bold text-gray-400 w-4">{idx + 1}.</span>
                              <input 
                                  type="email" 
                                  value={slot.email} 
                                  onChange={(e) => handleEmailChange(idx, 'email', e.target.value)}
                                  placeholder="admin@example.com"
                                  className="flex-grow p-2 border rounded text-sm"
                              />
                              <label className="flex items-center gap-2 cursor-pointer">
                                  <input 
                                      type="checkbox" 
                                      checked={slot.active} 
                                      onChange={(e) => handleEmailChange(idx, 'active', e.target.checked)}
                                      className="h-4 w-4 text-green-600 rounded"
                                  />
                                  <span className={`text-xs font-bold ${slot.active ? 'text-green-600' : 'text-gray-400'}`}>
                                      {slot.active ? 'Active' : 'Inactive'}
                                  </span>
                              </label>
                          </div>
                      ))}
                  </div>
              </div>

              {/* --- ADMIN PHONES --- */}
              <div>
                  <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                      <FaPhone className="text-green-500" /> Admin Phones (SMS)
                  </h3>
                  <div className="bg-green-50 p-3 rounded text-xs text-green-800 mb-4 flex items-start gap-2">
                      <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                      <p>
                          Enter phone numbers to receive SMS alerts. 
                          <strong> Every email alert triggers a corresponding SMS to these numbers.</strong>
                      </p>
                  </div>

                  <div className="space-y-3">
                      {config.admin_phones.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                              <span className="text-xs font-bold text-gray-400 w-4">{idx + 1}.</span>
                              <input 
                                  type="tel" 
                                  value={slot.phone} 
                                  onChange={(e) => handlePhoneChange(idx, 'phone', e.target.value)}
                                  placeholder="+234..."
                                  className="flex-grow p-2 border rounded text-sm"
                              />
                              <label className="flex items-center gap-2 cursor-pointer">
                                  <input 
                                      type="checkbox" 
                                      checked={slot.active} 
                                      onChange={(e) => handlePhoneChange(idx, 'active', e.target.checked)}
                                      className="h-4 w-4 text-green-600 rounded"
                                  />
                                  <span className={`text-xs font-bold ${slot.active ? 'text-green-600' : 'text-gray-400'}`}>
                                      {slot.active ? 'Active' : 'Inactive'}
                                  </span>
                              </label>
                          </div>
                      ))}
                  </div>
              </div>

          </div>

          {/* --- BANK ACCOUNTS (for customers who pay by transfer) --- */}
          <div className="p-6 border-t border-gray-200">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-1">
                  <FaUniversity className="text-purple-600" /> Bank Accounts (Pay by Transfer)
              </h3>
              <div className="bg-purple-50 border border-purple-100 rounded p-3 mb-4">
                  <p className="text-sm text-purple-800">
                      These accounts are shown to customers who cannot pay online, so they can
                      transfer directly. Only <strong>Active</strong> accounts are shown. Customers
                      then contact support and an admin funds their wallet.
                  </p>
              </div>

              <div className="space-y-4">
                  {config.bank_accounts.map((slot, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-bold text-gray-400">Account {idx + 1}</span>
                              <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                      type="checkbox"
                                      checked={slot.active}
                                      onChange={(e) => handleBankChange(idx, 'active', e.target.checked)}
                                      className="h-4 w-4 text-green-600 rounded"
                                  />
                                  <span className={`text-xs font-bold ${slot.active ? 'text-green-600' : 'text-gray-400'}`}>
                                      {slot.active ? 'Active (shown to customers)' : 'Inactive'}
                                  </span>
                              </label>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <input
                                  type="text"
                                  value={slot.bank_name}
                                  onChange={(e) => handleBankChange(idx, 'bank_name', e.target.value)}
                                  placeholder="Bank name (e.g. GTBank)"
                                  className="p-2 border rounded text-sm"
                              />
                              <input
                                  type="text"
                                  inputMode="numeric"
                                  value={slot.account_number}
                                  onChange={(e) => handleBankChange(idx, 'account_number', e.target.value.replace(/[^0-9]/g, ''))}
                                  placeholder="Account number"
                                  maxLength={12}
                                  className="p-2 border rounded text-sm"
                              />
                              <input
                                  type="text"
                                  value={slot.account_name}
                                  onChange={(e) => handleBankChange(idx, 'account_name', e.target.value)}
                                  placeholder="Account name"
                                  className="p-2 border rounded text-sm"
                              />
                          </div>
                      </div>
                  ))}
              </div>
          </div>

          <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button 
                  onClick={handleSave} 
                  disabled={saving} 
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg shadow-md flex items-center gap-2"
              >
                  {saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Configuration
              </button>
          </div>
      </div>
    </div>
  );
}

export default AdminNotificationSettings;